import React, { useEffect, useState, useRef } from 'react'
import { Spinner, Button, Form } from 'react-bootstrap'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Countdown from 'react-countdown'

import { fetchUserData, showCustomDomainModal, showMessageModal } from '../../Manage/actions'
import { addUpgradeReason, hasFeature, pluralize, onUpgradePlan, isOnFreeTrial } from '../../utils/general'
import {
  autoConnectDomainAdd,
  autoConnectDomainFailed,
  autoConnectDomainRetry,
  deleteDomain,
  clearDomainCache,
  updateAutoRenew
} from '../../services/custom-domain'
import { PRO_CUSTOM_DOMAIN_LIMIT, PLANS } from '../../constants/plans'
import EntriModal from './EntriModal'
import { fetchEmailAccounts } from '../../services/email/enom'
import LinkButton from '../LinkButton'
import CustomDomainModal from 'components/CustomDomainModal/CustomDomainModal'
import PhpCustomDomainModal from 'components/CustomDomainModal/PhpCustomDomainModal'
import Details from './components/Details'
import CustomDomainBanner from './components/CustomDomainBanner'
import ProjectSelectorModal from '../CustomDomainModal/components/ProjectSelectorModal'
import CustomDomainSelectorModal from '../CustomDomainSelectorModal/CustomDomainSelectorModal'
import { ENTRI_APP_ID } from '../../constants'
import { F_CUSTOM_DOMAIN } from '../../constants/plans/constants'
import CustomModal from 'components/CustomModal/CustomModal'
import SuccessBox from 'components/CreateUpdateSiteModal/components/SuccessBox'
import { ACTION_UPDATE } from 'components/CreateUpdateSiteModal/interface'
import { clickedCustomDomain } from 'utils/sendy'
import { showUpgradeCardModal } from 'actions'
import CustomTable from '../CustomTable'
import UpgradePromptModal from './components/UpgradePromptModal'
import ConfirmationModal from '../ConfirmationModal'
import UploadOptionsModal from './components/UploadOptionsModal'

import './CustomDomain.css'
import {
  STATUS_CONNECTED,
  STATUS_INIT,
  STATUS_FAILED,
  STATUS_DISABLED,
  STATUS_INCOMPLETE,
  getStatusLabel
} from '../../constants/customDomainStatus'

export { STATUS_CONNECTED, getStatusLabel }

const PENDING_CUSTOM_DOMAIN_PURCHASE_KEY = 'pendingCustomDomainPurchase'

const handleContinueSetup = (dispatch, record) => {
  if (record?.wasmerDnsRecord) {
    localStorage.setItem('isServerFileForCustomDomain', 'true')
  } else {
    localStorage.removeItem('isServerFileForCustomDomain')
  }
  dispatch(
    showCustomDomainModal({
      show: true,
      data: {
        ...record,
        domain: record?.domain,
        records: formatRecord(record?.CNAMEInfo, record?.cloudfrontDomain, record?.isRootDomain)
      }
    })
  )
}

const confirmDeleteMessage = (domain) => {
  return `Are you sure you want to <b>${domain?.status === 'Connected' ? 'DISABLE' : 'DELETE'}</b> this custom domain?${domain?.status === 'Connected' ? '<br /><b>All associated sites will also be deleted</b>.' : ''}`
}

const errorMessageDelete = {
  title: 'Delete failed',
  message: 'Unable to delete domain, please contact support.',
  error: true
}

export const clearCacheSuccessMessage = {
  title: 'Cache cleared',
  message: 'Your latest changes will be live on your custom domain within a few minutes.',
  error: false
}

export const clearCacheErrorMessage = {
  title: 'Clear cache failed',
  message: 'Unable to clear the cache for this domain, please contact support.',
  error: true
}

const inProgressMessage = {
  title: 'Domain update in progress',
  message:
    'Your domain is updating. This can take between 5-10 minutes. Please visit back later to permanently delete the domain.',
  error: true
}

export const domainErrorMessage = {
  show: true,
  error: true,
  title: 'Unable to set up domain',
  message: 'Please contact support.'
}

export const domainValidateErrorMessage = {
  show: true,
  error: true,
  title: 'Please try again later',
  message:
    "It can take up to a few hours to validate. Please try again later or contact support if it's taking longer than 24 hours."
}

const domainInitiatedMessage = {
  show: true,
  title: 'DNS Successfully updated',
  message: 'We need to validate your set-up. Please wait for at least 15 minutes to verify this.'
}

const getEntriConfig = (token, domain, certName, certValue, cfName, replaceWWW) => {
  const config = {
    applicationId: ENTRI_APP_ID,
    prefilledDomain: domain,
    token,
    dnsRecords: [
      {
        type: 'CNAME',
        host: certName.split('.')[0],
        value: certValue,
        ttl: 300
      },
      {
        type: 'CNAME',
        host: '*',
        value: cfName,
        ttl: 300
      }
    ],
    embeddedMode: {
      selector: '#entri-container'
    }
  }

  if (replaceWWW) {
    config.dnsRecords.push(
      {
        type: 'CNAME',
        host: 'www',
        value: cfName,
        ttl: 300
      },
      {
        type: 'A',
        host: '@',
        value: '18.168.91.127',
        ttl: 300
      }
    )
  }

  return config
}

const pad = (num) => (num.toString().length === 1 ? `0${num}` : num)
const renderer =
  (domain, onAction, onDelete) =>
  ({ minutes, seconds, completed }) => {
    if (completed) {
      return (
        <div className="flex-records">
          <LinkButton label="Validate" onClick={() => onAction(domain, 'validate')} icon="/assets/icons/validate.svg" />
          {!domain.isEnomDomain && (
            <LinkButton label="Delete" onClick={() => onDelete(domain)} icon="/assets/icons/trash.svg" />
          )}
        </div>
      )
    } else {
      return (
        <div className="flex-records justify-center align-items-center">
          <span className="me-2">
            Please wait {pad(minutes)}:{pad(seconds)}
          </span>
          <LinkButton
            label="View Records"
            onClick={() => onAction(domain, 'viewrecords')}
            icon="/assets/icons/details.svg"
          />
        </div>
      )
    }
  }

const InitiatedView = ({ domain, onAction, onDelete }) => (
  <span className="text-sm grey">
    <Countdown
      date={new Date(domain.created).getTime() + 900000} // 15mins
      renderer={renderer(domain, onAction, onDelete)}
    />
  </span>
)

const formatRecord = (CNAMEInfo, cloudfrontDomain, isRootDomain) => {
  let formattedRecords = []
  if (isRootDomain) {
    formattedRecords = [
      {
        type: 'A',
        host: '@',
        value: '18.168.91.127'
      },
      {
        type: 'CNAME',
        host: 'www',
        value: cloudfrontDomain
      },
      {
        type: 'CNAME',
        host: '*',
        value: cloudfrontDomain
      },
      {
        type: 'CNAME',
        host: CNAMEInfo.Name,
        value: CNAMEInfo.Value
      }
    ]
  } else {
    formattedRecords = [
      {
        type: 'CNAME',
        host: '*',
        value: cloudfrontDomain
      },
      {
        type: 'CNAME',
        host: CNAMEInfo.Name,
        value: CNAMEInfo.Value
      }
    ]
  }

  return formattedRecords
}

const getModalDataAsJSON = (record) => {
  const data = JSON.stringify({
    ...record,
    domain: record?.domain,
    records: formatRecord(record?.CNAMEInfo, record?.cloudfrontDomain, record?.isRootDomain)
  })

  return data
}

const CustomDomain = ({
  dispatch,
  className,
  customDomains = [],
  openModal,
  productId,
  customDomainsLimit,
  onSiteAction,
  userProfile,
  disabled,
  onOpenEmailSubscription,
  modalData // Added modalData prop
}) => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [domains, setDomains] = useState(customDomains)
  const [emailData, setEmailData] = useState({})
  const [detailsModal, setDetailsModal] = useState({ show: false, records: [], domainRecord: {} })
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
  const [showCancelAutoRenewModal, setShowCancelAutoRenewModal] = useState(false)
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false)
  const [domainToCancel, setDomainToCancel] = useState(null)
  const [modalConfirmationShow, setModalConfirmationShow] = useState(false)
  const [domainToDelete, setDomainToDelete] = useState()
  const [publishSuccessData, setPublishSuccessData] = useState(null)
  const [caaErrorDomain, setCaaErrorDomain] = useState(null)
  const [caaConfirmed, setCaaConfirmed] = useState(false)
  const [showEntriModal, setShowEntriModal] = useState(false)
  const [entriIframeConfig, setEntriIframeConfig] = useState(null)
  const [showUploadOptionsModal, setShowUploadOptionsModal] = useState(false)
  const [domainToUpload, setDomainToUpload] = useState(null)
  const [showProjectSelectorModal, setShowProjectSelectorModal] = useState(false)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const pendingDomainOpenRequestedRef = useRef(false)

  // Reset the checkbox every time a new CAA error appears
  useEffect(() => {
    if (caaErrorDomain) setCaaConfirmed(false)
  }, [caaErrorDomain])

  const onClose = () => {
    dispatch(showCustomDomainModal(false))
  }

  const onFail = (message) => {
    dispatch(showMessageModal(message))
  }

  const onCAAError = (context) => {
    dispatch(showCustomDomainModal(false))
    setCaaErrorDomain(context)
  }

  const handleCAARetry = async () => {
    if (!caaErrorDomain) return
    const { originalDomain, isRootDomain, modalData } = caaErrorDomain
    setCaaErrorDomain(null)
    setLoading(true)
    try {
      await deleteDomain({ domain: originalDomain })
    } catch (e) {
      console.error('CAA retry: delete error (continuing)', e)
    }
    try {
      const { data } = await autoConnectDomainAdd({ domain: originalDomain, replaceWWW: isRootDomain })
      if (data?.domain) {
        localStorage.setItem('start_time', new Date().toISOString())
        dispatch(fetchUserData(false))
        if (modalData) {
          dispatch(showCustomDomainModal({ show: true, data: modalData }))
        } else {
          dispatch(showCustomDomainModal(true))
        }
      } else {
        dispatch(showMessageModal(domainErrorMessage))
      }
    } catch (e) {
      dispatch(showMessageModal(domainErrorMessage))
    } finally {
      setLoading(false)
    }
  }

  const domainLimit = customDomainsLimit || PRO_CUSTOM_DOMAIN_LIMIT[productId] || 5

  useEffect(() => {
    setDomains(customDomains)
    fetchEmailDataForDomains(customDomains)
  }, [customDomains])

  useEffect(() => {
    const pendingCustomDomainPurchase = localStorage.getItem(PENDING_CUSTOM_DOMAIN_PURCHASE_KEY)

    if (!pendingCustomDomainPurchase) {
      pendingDomainOpenRequestedRef.current = false
      return
    }

    if (!hasFeature(userProfile?.productId, F_CUSTOM_DOMAIN)) {
      return
    }

    localStorage.removeItem('isServerFileForCustomDomain')

    if (!openModal) {
      pendingDomainOpenRequestedRef.current = true
      dispatch(showCustomDomainModal(true))
      return
    }

    if (!pendingDomainOpenRequestedRef.current) {
      localStorage.removeItem(PENDING_CUSTOM_DOMAIN_PURCHASE_KEY)
    }
  }, [dispatch, openModal, userProfile?.productId])

  const fetchEmailDataForDomains = async (domainsList) => {
    const emailDataMap = {}

    for (const domain of domainsList) {
      try {
        const emails = await fetchEmailAccounts(domain.domain)
        if (emails.length > 0) {
          emailDataMap[domain.domain] = {
            hasEmail: true,
            emailCount: emails.length
          }
        } else {
          emailDataMap[domain.domain] = {
            hasEmail: false,
            emailCount: 0
          }
        }
      } catch {
        emailDataMap[domain.domain] = {
          hasEmail: false,
          emailCount: 0
        }
      }
    }

    setEmailData(emailDataMap)
  }

  const entriDataRef = useRef(null)

  const onEntriClose = async (e) => {
    setShowEntriModal(false)
    if (e?.detail?.success) {
      try {
        await autoConnectDomainRetry({ domain: e.detail.domain, registrar: 'entri' })
      } catch (err) {
        dispatch(showMessageModal(domainErrorMessage))
        return
      }
      history.replace({ pathname: '/manage' })
      dispatch(fetchUserData(false))
      dispatch(showMessageModal(domainInitiatedMessage))
    } else {
      autoConnectDomainFailed({ domain: e.detail.domain }).finally(() => {
        const savedData = entriDataRef.current
        dispatch(
          showCustomDomainModal({
            show: true,
            data: {
              ...(savedData || {}),
              cancelled: true
            }
          })
        )
        dispatch(fetchUserData())
      })
    }
  }

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'ENTRI_CLOSE') {
        onEntriClose({ detail: event.data.detail })
      }
    }
    const handleEntriClose = (e) => onEntriClose(e)
    window.addEventListener('message', handleMessage)
    window.addEventListener('onEntriClose', handleEntriClose)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('onEntriClose', handleEntriClose)
    }
  }, [])

  const setupWithEntri = (domain, token, cert, cfName, replaceWWW, fullDnsData) => {
    entriDataRef.current = fullDnsData ? { ...fullDnsData, replaceWWW } : { domain, token, cert, cfName, replaceWWW }
    dispatch(showCustomDomainModal(false))
    setEntriIframeConfig(getEntriConfig(token, domain, cert.name, cert.value, cfName, replaceWWW))
    setShowEntriModal(true)
  }

  const handleCheckEntri = async (payload, formik) => {
    try {
      const { data } = await autoConnectDomainAdd(payload)
      if (data?.domain) {
        entriDataRef.current = data
        const { domain, token, cert, cfName } = data
        formik.setFieldValue(`dnsRecords.data`, data)
        const domainCheck = await window.entri.checkDomain(
          domain,
          getEntriConfig(token, domain, cert.name, cert.value, cfName, payload.replaceWWW)
        )
        const isManual = domainCheck?.setupType.toLowerCase() === 'manual' ? 'manual' : 'automatic'
        dispatch(fetchUserData(false))
        return isManual
      }
    } catch {
      dispatch(showMessageModal(domainErrorMessage))
    }
  }

  const showPayWall = () => {
    if (isOnFreeTrial(userProfile)) {
      setShowUpgradePrompt(true)
      return true
    }
    if (!hasFeature(userProfile?.productId, F_CUSTOM_DOMAIN)) {
      addUpgradeReason('Add Custom Domain')
      onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
      return true // Indicates that the paywall was triggered
    } else if (customDomains.length >= domainLimit) {
      dispatch(
        showUpgradeCardModal({
          title: 'Upgrade to link more domains',
          desc: `Your current plan only allows ${domainLimit} custom ${pluralize('domain', 'domains', domainLimit)}`,
          cta: 'Add more domains',
          onClick: () => onUpgradePlan({ planId: PLANS.PRO_U.id, userProfile }, dispatch)
        })
      )
      return true // Indicates that the paywall was triggered
    }

    return false // Indicates that the paywall was not triggered
  }

  const onAddDomain = () => {
    dispatch(showCustomDomainModal(true))
    clickedCustomDomain()
    localStorage.removeItem('publishToCustomDomain')
  }

  const handleRetry = async (record) => {
    if (record?.wasmerDnsRecord) {
      localStorage.setItem('isServerFileForCustomDomain', 'true')
    } else {
      localStorage.removeItem('isServerFileForCustomDomain')
    }
    if (record?.isEnomDomain) {
      setLoading(true)
      try {
        const { data } = await autoConnectDomainAdd({
          domain: record.domain,
          replaceWWW: true
        })
        if (data?.domain) {
          dispatch(showCustomDomainModal(true))
          localStorage.setItem('start_time', record.created)
          localStorage.setItem('view_records', JSON.stringify(data))
          dispatch(fetchUserData(false))
        }
        setLoading(false)
      } catch {
        setLoading(false)
        dispatch(showMessageModal(domainErrorMessage))
      }
    } else {
      dispatch(showCustomDomainModal(true))
      localStorage.setItem('retry', getModalDataAsJSON(record))
    }
  }

  const handleAction = async (record, action) => {
    if (record?.wasmerDnsRecord) {
      localStorage.setItem('isServerFileForCustomDomain', 'true')
    } else {
      localStorage.removeItem('isServerFileForCustomDomain')
    }
    switch (action) {
      case 'validate':
        dispatch(showCustomDomainModal(true))
        localStorage.setItem('validate', getModalDataAsJSON(record))
        break
      case 'retry':
        await handleRetry(record)
        break
      case 'viewrecords':
        dispatch(showCustomDomainModal(true))
        localStorage.setItem('start_time', record.created)
        localStorage.setItem('view_records', getModalDataAsJSON(record))
        break
      default:
        break
    }
  }

  const onUpload = (d) => {
    localStorage.setItem('active_custom_domain', `.${d.domain}`)
    localStorage.setItem('active_custom_subdomain', d?.subdomain ? d?.subdomain : 'www')
    onSiteAction('CREATE')
  }

  // Reuses the "Continue"/"Retry"/"Upload" flows so the add-domain modal can offer them
  // when the domain a user is typing already exists on their account, matching whichever
  // action the domains table exposes for that record's status.
  const onContinueSetupFromAddModal = (record) => handleContinueSetup(dispatch, record)

  const onRetryFromAddModal = (record) => handleRetry(record)

  const onUploadToExistingDomain = (record) => {
    onClose()
    onUpload(record)
  }

  const onOpenUploadOptions = (domainRecord) => {
    setDomainToUpload(domainRecord)
    setShowUploadOptionsModal(true)
  }

  const onUploadNewSite = () => {
    setShowUploadOptionsModal(false)
    if (domainToUpload) {
      onUpload(domainToUpload)
    }
  }

  const onPublishExistingSite = () => {
    setShowUploadOptionsModal(false)
    setShowProjectSelectorModal(true)

    // Update URL to deeplink format
    if (domainToUpload) {
      const fullDomain = domainToUpload.subdomain
        ? `${domainToUpload.subdomain}.${domainToUpload.domain}`
        : domainToUpload.domain
      history.replace({
        pathname: '/manage/domain/update',
        search: `?site=${fullDomain}`
      })
    }
  }

  const onProjectSelected = (project) => {
    setSelectedProject(project)
    setShowProjectSelectorModal(false)
    setShowPublishModal(true)
  }

  const closeUploadFlow = () => {
    setShowUploadOptionsModal(false)
    setShowProjectSelectorModal(false)
    setShowPublishModal(false)
    setSelectedProject(null)
    setDomainToUpload(null)

    // Revert URL back to /manage
    history.replace({ pathname: '/manage' })
  }

  const onDelete = (domain) => {
    setDomainToDelete(domain)
    setModalConfirmationShow(true)
  }

  const deleteCustomDomain = () => {
    setModalConfirmationShow(false)
    setLoading(true)

    deleteDomain({ domain: domainToDelete.domain })
      .then(({ data }) => {
        setLoading(false)
        if (data.success) {
          if (data.status === 'InProgress') {
            dispatch(showMessageModal(inProgressMessage))
            dispatch(fetchUserData())
          } else {
            dispatch(fetchUserData())
          }
        } else {
          dispatch(showMessageModal(errorMessageDelete))
        }
      })
      .catch(() => {
        setLoading(false)
        dispatch(showMessageModal(errorMessageDelete))
      })
  }

  const onClearCache = (domain) => {
    setLoading(true)

    clearDomainCache({ domain: domain.domain })
      .then(({ data }) => {
        setLoading(false)
        dispatch(showMessageModal(data?.success ? clearCacheSuccessMessage : clearCacheErrorMessage))
      })
      .catch(() => {
        setLoading(false)
        dispatch(showMessageModal(clearCacheErrorMessage))
      })
  }

  const onToggleAutoRenew = (domain) => {
    setDomainToCancel(domain)
    setShowCancelAutoRenewModal(true)
  }

  const handleToggleAutoRenew = () => {
    setLoading(true)
    const targetAutoRenew = domainToCancel?.autoRenew === false
    updateAutoRenew({ domain: domainToCancel?.domain, autoRenew: targetAutoRenew })
      .then(() => {
        setLoading(false)
        setShowCancelAutoRenewModal(false)
        setShowCancelSuccessModal(true)
        dispatch(fetchUserData(false))
      })
      .catch((err) => {
        setLoading(false)
        dispatch(
          showMessageModal({
            title: 'Error',
            message:
              err.response?.data?.message ||
              `Failed to ${targetAutoRenew ? 'turn on' : 'turn off'} auto-renew. Please contact support.`,
            error: true
          })
        )
      })
  }

  const getCancellationDate = (domain) => {
    const date = domain?.expiryDate || domain?.renewalDate || domain?.nextPaymentDate
    if (!date) return 'the end of your subscription period'
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(date))
  }

  const setShowDetailsModal = (domain) => {
    setDetailsModal({
      show: true,
      records: formatRecord(domain?.CNAMEInfo, domain?.cloudfrontDomain, domain?.isRootDomain),
      domainRecord: domain
    })
  }

  const onModalClose = () => {
    dispatch(showCustomDomainModal(false))
    dispatch(fetchUserData(false))
  }

  const handleSuccessClose = () => {
    setPublishSuccessData(null)
  }

  const handleSuccessReset = (site) => {
    setPublishSuccessData(null)
    onSiteAction(ACTION_UPDATE, site)
  }

  if (publishSuccessData) {
    return (
      <CustomModal
        show={true}
        onHide={handleSuccessClose}
        centered
        size="md"
        className="success_modal"
        enforceFocus={false}
        mobileCloseButton={false}
      >
        <SuccessBox
          site={publishSuccessData.site}
          files={publishSuccessData.files}
          userProfile={publishSuccessData.userProfile}
          onClose={handleSuccessClose}
          onReset={handleSuccessReset}
        />
      </CustomModal>
    )
  }

  const getActions = (domain) => {
    const status = getStatusLabel(domain.status)
    const autoRenewLabel = domain.autoRenew === false ? 'Turn on auto-renew' : 'Turn off auto-renew'
    const autoRenewIcon = domain.autoRenew === false ? 'check.svg' : 'trash.svg'

    if (status === 'VALIDATED') {
      const menuItems = [
        {
          icon: 'email.svg',
          label: 'Email',
          onAction: () => onOpenEmailSubscription?.(domain.domain),
          isVisible: userProfile?.customerId && domain.isEnomDomain
        },
        { icon: 'details.svg', label: 'Details', onAction: () => setShowDetailsModal(domain), isVisible: true },
        { icon: 'sync.svg', label: 'Clear cache', onAction: () => onClearCache(domain), isVisible: true },
        {
          icon: autoRenewIcon,
          label: autoRenewLabel,
          onAction: () => onToggleAutoRenew(domain),
          isVisible: domain.isEnomDomain
        },
        { icon: 'trash.svg', label: 'Delete', onAction: () => onDelete(domain), isVisible: !domain.isEnomDomain }
      ].filter((item) => item.isVisible)

      return [
        { icon: '/assets/icons/replace.svg', label: 'Upload', onClick: () => onUpload(domain) },
        {
          type: 'action_menu',
          props: { icon: '/assets/icons/menu_horizontal.svg', isCustomDomain: true, items: menuItems }
        }
      ]
    }

    const allActions = [
      {
        type: 'custom_button',
        component: <InitiatedView domain={domain} onDelete={onDelete} onAction={handleAction} />,
        isVisible: status === STATUS_INIT
      },
      {
        icon: '/assets/icons/retry.svg',
        label: 'Continue',
        onClick: () => handleContinueSetup(dispatch, domain),
        isVisible: status === STATUS_INCOMPLETE
      },
      {
        icon: '/assets/icons/retry.svg',
        label: 'Retry',
        onClick: () => handleAction(domain, 'retry'),
        isVisible: status === STATUS_FAILED
      },
      {
        icon: '/assets/icons/replace.svg',
        label: 'Upload',
        onClick: () => onUpload(domain),
        isVisible:
          status !== STATUS_INIT &&
          status !== STATUS_FAILED &&
          status !== STATUS_DISABLED &&
          status !== STATUS_INCOMPLETE
      },
      {
        icon: '/assets/icons/email.svg',
        label: 'Email',
        onClick: () => onOpenEmailSubscription?.(domain.domain),
        isVisible:
          status !== STATUS_INIT &&
          status !== STATUS_FAILED &&
          status !== STATUS_DISABLED &&
          status !== STATUS_INCOMPLETE &&
          userProfile?.customerId &&
          domain.isEnomDomain
      },
      {
        icon: '/assets/icons/details.svg',
        label: 'Details',
        onClick: () => setShowDetailsModal(domain),
        isVisible:
          status !== STATUS_INIT &&
          status !== STATUS_FAILED &&
          status !== STATUS_DISABLED &&
          status !== STATUS_INCOMPLETE
      },
      {
        icon: '/assets/icons/trash.svg',
        label: 'Delete',
        onClick: () => onDelete(domain),
        isVisible: !domain.isEnomDomain && status !== STATUS_INIT
      },
      {
        type: 'action_menu',
        props: {
          icon: '/assets/icons/menu_horizontal.svg',
          isCustomDomain: true,
          label: autoRenewLabel,
          iconItem: autoRenewIcon,
          onAction: () => onToggleAutoRenew(domain)
        },
        isVisible: domain.isEnomDomain && status !== STATUS_INIT && status !== STATUS_FAILED
      }
    ]

    return allActions.filter((action) => action.isVisible)
  }

  const headers = [
    { key: 'status', text: 'Status', icon: '', cellType: 'status' },
    { key: 'domain', text: 'Domain', icon: '', cellType: 'normal_text' },
    { key: 'actions', text: 'Actions', icon: '', cellType: 'actions' }
  ]

  const data = domains.map((d) => ({
    domain: d.domain,
    status: {
      status: getStatusLabel(d.status).toLowerCase(),
      text: getStatusLabel(d.status).toLowerCase()
    },
    actions: getActions(d),
    hasEmail: emailData[d.domain]?.hasEmail || false,
    emailCount: emailData[d.domain]?.emailCount || 0
  }))

  const headerButtons = [
    {
      icon: '/assets/icons/plus-circle.svg',
      text: 'Add new',
      onClick: onAddDomain,
      className: `add-new-button${disabled ? ' disabled' : ''}`
    }
    // Add more buttons
  ]

  const isServerFile = localStorage.getItem('isServerFileForCustomDomain') === 'true'

  return (
    <div className={className}>
      {isServerFile ? (
        <PhpCustomDomainModal
          show={openModal}
          onClose={onClose}
          domains={customDomains.map((d) => d.domain)}
          onSelectEntri={setupWithEntri}
          onFail={onFail}
          setDomains={setDomains}
          showPayWall={showPayWall}
          setPublishSuccessData={setPublishSuccessData}
          modalData={modalData}
          handleCheckEntri={handleCheckEntri}
          refreshProfile={() => dispatch(fetchUserData(false))}
          onSiteAction={onSiteAction}
        />
      ) : (
        <CustomDomainModal
          show={openModal}
          onClose={onClose}
          onSelectEntri={setupWithEntri}
          onFail={(msg) => dispatch(showMessageModal(msg))}
          setDomains={setDomains}
          domains={domains}
          showPayWall={showPayWall}
          setPublishSuccessData={setPublishSuccessData}
          modalData={modalData}
          handleCheckEntri={handleCheckEntri}
          refreshProfile={() => dispatch(fetchUserData(false))}
          onSiteAction={onSiteAction}
          onCAAError={onCAAError}
          onContinueSetup={onContinueSetupFromAddModal}
          onRetryDomain={onRetryFromAddModal}
          onUploadToDomain={onUploadToExistingDomain}
        />
      )}
      <ConfirmationModal
        title="Delete site"
        message={confirmDeleteMessage(domainToDelete)}
        show={modalConfirmationShow}
        handlePositive={deleteCustomDomain}
        handleNegative={() => setModalConfirmationShow(false)}
      />
      <UpgradePromptModal
        show={showUpgradePrompt}
        onHide={() => setShowUpgradePrompt(false)}
        userProfile={userProfile}
        dispatch={dispatch}
      />
      {/* CAA record error modal */}
      <CustomModal show={!!caaErrorDomain} onHide={() => setCaaErrorDomain(null)} centered>
        <div className="ps-3 pe-3">
          <div className="text-center mb-4 mt-4">
            <h4 className="mt-3 mb-4">Domain setup failed</h4>
            <p className="grey w-75 m-auto mb-4">
              The domain setup failed because you didn&apos;t add the CAA record. Please add the record and try again.
            </p>
            <div className="d-flex justify-content-center">
              <Form.Check
                id="caa-confirmed-checkbox"
                type="checkbox"
                label="I have added the CAA record to my domain"
                checked={caaConfirmed}
                onChange={(e) => setCaaConfirmed(e.target.checked)}
                className="text-start"
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
          <div className="d-flex gap-2 mb-3">
            <Button
              variant="outline-primary"
              className="w-100"
              onClick={() => setCaaErrorDomain(null)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button variant="primary" className="w-100" onClick={handleCAARetry} disabled={loading || !caaConfirmed}>
              {loading ? <Spinner animation="border" size="sm" /> : 'Retry'}
            </Button>
          </div>
        </div>
      </CustomModal>
      <CustomModal show={showCancelAutoRenewModal} onHide={() => setShowCancelAutoRenewModal(false)} centered>
        <div className="p-4">
          <h4 className="mb-3 text-center">
            {domainToCancel?.autoRenew === false ? 'Turn on auto-renew' : 'Turn off auto-renew'}
          </h4>
          <p className="mb-4">
            {domainToCancel?.autoRenew === false ? (
              <>Are you sure you want to turn on automatic renewal for your custom domain?</>
            ) : (
              <>
                Are you sure you want to turn off automatic renewal for your custom domain? Your domain will be deleted
                on <b>{getCancellationDate(domainToCancel)}</b>
              </>
            )}
          </p>
          <div className="d-flex gap-3">
            <Button variant="outline-primary" className="w-100" onClick={() => setShowCancelAutoRenewModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" className="w-100" onClick={handleToggleAutoRenew} disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : domainToCancel?.autoRenew === false ? (
                'Turn on'
              ) : (
                'Turn off'
              )}
            </Button>
          </div>
        </div>
      </CustomModal>
      <CustomModal show={showCancelSuccessModal} onHide={() => setShowCancelSuccessModal(false)} centered>
        <div className="p-4">
          <div className="mb-3 text-center">
            <img src="/assets/icons/check.svg" alt="Success" width={48} />
          </div>
          <p className="mb-4 font-18 text-center">
            Auto-renew has been {domainToCancel?.autoRenew === false ? 'turned on' : 'turned off'} for your domain
          </p>
          <Button variant="primary" className="w-100" onClick={() => setShowCancelSuccessModal(false)}>
            Close
          </Button>
        </div>
      </CustomModal>
      <Details
        onClose={() => setDetailsModal({ show: false, records: [], domainRecord: {} })}
        dnsData={detailsModal}
        refreshProfile={() => dispatch(fetchUserData(false))}
        onOpenUpload={(domainRecord) => {
          setDetailsModal({ show: false, records: [], domainRecord: {} })
          onOpenUploadOptions(domainRecord)
        }}
      />
      <UploadOptionsModal
        show={showUploadOptionsModal}
        onHide={closeUploadFlow}
        domainRecord={domainToUpload}
        onPublishExisting={onPublishExistingSite}
        onUploadNew={onUploadNewSite}
      />
      {showProjectSelectorModal && domainToUpload && (
        <ProjectSelectorModal
          show={showProjectSelectorModal}
          onHide={closeUploadFlow}
          onSelectProject={onProjectSelected}
          customDomain={
            domainToUpload.subdomain
              ? `${domainToUpload.subdomain}.${domainToUpload.domain}`
              : `www.${domainToUpload.domain}`
          }
        />
      )}
      {showPublishModal && selectedProject && domainToUpload && (
        <CustomDomainSelectorModal
          show={showPublishModal}
          onHide={closeUploadFlow}
          validatedDomains={[domainToUpload]}
          site={selectedProject}
          onAddNewDomain={() => {
            closeUploadFlow()
            onAddDomain()
          }}
          onUpdate={() => {
            closeUploadFlow()
            dispatch(fetchUserData(false))
          }}
        />
      )}
      <EntriModal
        show={showEntriModal}
        onHide={() => setShowEntriModal(false)}
        entriIframeConfig={entriIframeConfig}
        onManualSetup={() => {
          setShowEntriModal(false)
          const savedData = entriDataRef.current
          dispatch(
            showCustomDomainModal({
              show: true,
              data: {
                ...(savedData || {}),
                cancelled: true
              }
            })
          )
        }}
      />
      <div className="custom-domain-table-wrapper">
        {loading ? (
          <Spinner animation="border" variant="dark" />
        ) : (
          <CustomTable
            title="Custom Domains"
            data={data}
            headers={headers}
            badge={
              customDomains.length
                ? `${customDomains.length} / ${domainLimit} domain${domainLimit > 0 ? 's' : ''}`
                : null
            }
            headerButtons={headerButtons}
            emptyStateMessage="No custom domains"
            emptyStateCustomElement={<CustomDomainBanner onSearch={onAddDomain} />}
          />
        )}
      </div>
    </div>
  )
}

const mapStateToProps = ({ manage }) => ({
  openModal: manage.customDomainModalVisible,
  modalData: manage.customDomainModalData,
  userProfile: manage.userProfile
})

export default connect(mapStateToProps)(CustomDomain)
